import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { Spacer } from '../../common/components/v2';
import { UpdatedRateClass } from '../../common/constants/decision';
import {
  IRule,
  IRuleResults,
  IRuleSet,
} from '../detail/interfaces/IRuleResults';
import {
  RuleResultTab,
  RuleResultTabUnion,
} from '../detail/ruleResults/constants';
import { RuleResultsView } from '../detail/ruleResults/RuleResultsView';

import { Button } from '@getethos/ethos-design-system-v2';
import { FaLightbulb } from 'react-icons/fa';
import {
  filterToGuidelineRuleSets,
  isEmptyRule,
} from '../../common/helpers/rule-results/filter';
import { sortRuleSets } from '../../common/helpers/rule-results/sort';
import { filterVisibleDecisionChangeReasons } from '../detail/decision/decisionMessages';
import { CalculationInfo } from '../detail/interfaces/IDecision';
import {
  fetchPolicyRuleResult,
  getAvailablePPWBRuleResultDecisionLabels,
  getLatestPPWBDecisionByType,
  getPPWBDecisionTypeFromLabel,
  PPWBRuleResultDecisionLabel,
} from './helpers';

const orderRules = (
  ruleResults: IRuleResults | null,
  selectedTab: RuleResultTabUnion,
): IRuleResults | null => {
  if (!ruleResults) return null;

  const allRuleSets = ruleResults.ruleSets || [];
  const guidelineRuleSets: IRuleSet[] = sortRuleSets(
    filterToGuidelineRuleSets(allRuleSets),
  );

  // MDM Decision dont have conditional rule sets
  const conditionalRuleSets: IRuleSet[] = allRuleSets
    .map(ruleSet => ({
      ...ruleSet,
      matchedRules: ruleSet.matchedRules.filter((rule: IRule) =>
        isEmptyRule(rule),
      ),
    }))
    .filter(ruleSet => ruleSet.matchedRules.length > 0)
    .filter(
      ruleSet => !ruleSet.ruleSetName.toLowerCase().includes('(superset)'),
    );

  let allRuleSetsSorted = [...guidelineRuleSets, ...conditionalRuleSets];

  if (selectedTab === 'RIDERS') {
    allRuleSetsSorted = allRuleSets.filter(ruleSet =>
      ruleSet.matchedRules.some(
        rule => (rule.ineligibleRiders ?? []).length > 0,
      ),
    );
  }

  if (selectedTab === 'HIGHLIGHT') {
    allRuleSetsSorted = guidelineRuleSets
      .map(ruleSet => {
        const filteredMatchedRules = ruleSet.matchedRules.filter(rule => {
          return rule.rateClass !== UpdatedRateClass.PREFERRED_PLUS;
        });
        return filteredMatchedRules.length > 0
          ? { ...ruleSet, matchedRules: filteredMatchedRules }
          : null;
      })
      .filter((ruleSet): ruleSet is IRuleSet => ruleSet !== null);
  }

  return {
    ...ruleResults,
    ruleSets: allRuleSetsSorted,
  };
};

export const RuleResults = ({
  policyId,
  isGen2 = false,
  decisions,
}: {
  policyId: string;
  isGen2?: boolean;
  decisions: { [key: string]: any }[];
}) => {
  const fetchRequestIdRef = useRef(0);

  const availableDecisionTypes = useMemo(
    () => getAvailablePPWBRuleResultDecisionLabels(decisions),
    [decisions],
  );

  const [selectedDecisionType, setSelectedDecisionType] =
    useState<PPWBRuleResultDecisionLabel>('Engine');
  const [expanded, setExpanded] = useState(false);
  const [ruleResultData, setRuleResultData] = useState<IRuleResults | null>(
    null,
  );
  const [resultInprogress, setResultInprogress] = useState(false);
  const [resultReady, setResultReady] = useState(false);
  const [loadedDecisionId, setLoadedDecisionId] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] =
    useState<RuleResultTabUnion>('ALL_CONDITIONS');

  useEffect(() => {
    if (!availableDecisionTypes.includes(selectedDecisionType)) {
      setSelectedDecisionType(
        availableDecisionTypes.includes('Engine')
          ? 'Engine'
          : availableDecisionTypes[0],
      );
    }
  }, [availableDecisionTypes, selectedDecisionType]);

  const selectedDecision = useMemo(() => {
    const decisionType = getPPWBDecisionTypeFromLabel(selectedDecisionType);
    return getLatestPPWBDecisionByType(decisions, decisionType);
  }, [decisions, selectedDecisionType]);

  const selectedDecisionId = selectedDecision?.id;

  const fetchRuleResults = useCallback(
    async (decisionId: number) => {
      const requestId = ++fetchRequestIdRef.current;
      setResultInprogress(true);
      try {
        const result = await fetchPolicyRuleResult(
          policyId,
          decisionId.toString(),
        );
        if (requestId !== fetchRequestIdRef.current) {
          return;
        }
        setRuleResultData(result);
        setLoadedDecisionId(decisionId);
        setResultReady(true);
      } catch {
        if (requestId === fetchRequestIdRef.current) {
          setRuleResultData(null);
          setLoadedDecisionId(null);
          setResultReady(true);
        }
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setResultInprogress(false);
        }
      }
    },
    [policyId],
  );

  useEffect(() => {
    if (!expanded || !selectedDecisionId) {
      return;
    }
    if (selectedDecisionId === loadedDecisionId) {
      return;
    }
    fetchRuleResults(selectedDecisionId);
  }, [expanded, selectedDecisionId, loadedDecisionId, fetchRuleResults]);

  const handleDecisionTypeChange = (event: SelectChangeEvent) => {
    fetchRequestIdRef.current += 1;
    setSelectedDecisionType(event.target.value as PPWBRuleResultDecisionLabel);
    setRuleResultData(null);
    setResultReady(false);
    setLoadedDecisionId(null);
  };

  // Add check for riders data availability
  const hasRidersData = useMemo(() => {
    if (!ruleResultData?.ruleSets) return false;

    return ruleResultData.ruleSets.some(ruleSet =>
      ruleSet.matchedRules.some(
        rule => (rule.ineligibleRiders ?? []).length > 0,
      ),
    );
  }, [ruleResultData]);

  // Dynamic tabs based on data availability
  const tabs: RuleResultTabUnion[] = useMemo(() => {
    const baseTabs: RuleResultTabUnion[] = ['ALL_CONDITIONS', 'HIGHLIGHT'];
    return hasRidersData ? [...baseTabs, 'RIDERS'] : baseTabs;
  }, [hasRidersData]);

  const handleTabChange = (
    _: React.SyntheticEvent,
    newTabKey: RuleResultTabUnion,
  ) => {
    if (newTabKey) {
      setSelectedTab(newTabKey);
    }
  };

  const saharaDeclineToRuw = useMemo(() => {
    return !!ruleResultData?.ruleSets?.some(ruleSet =>
      ruleSet.matchedRules.some(rule =>
        rule.requirement?.includes('sahara_decline_to_ruw'),
      ),
    );
  }, [ruleResultData]);

  const getMessages = useCallback(() => {
    const tabMessages = getMessageFromDecision(
      selectedDecision as PPWBEngineDecision | undefined,
      saharaDeclineToRuw,
    );
    return tabMessages;
  }, [selectedDecision, saharaDeclineToRuw]);

  const lightbulbMessages = getMessages();

  const doExpand = () => {
    setExpanded(!expanded);
  };

  const orderedRuleResultData = {
    ruleResults: orderRules(ruleResultData, selectedTab),
    fetchRuleResultsInProgress: resultInprogress,
    fetchRuleResultsError: resultReady && ruleResultData === null,
  };

  return (
    <Box pt={8}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={-0.15} ml={-3.5} position="absolute">
            <IconButton
              style={{ width: '24px' }}
              size="small"
              onClick={doExpand}
            >
              <FontAwesomeIcon
                icon={faAngleRight}
                rotation={expanded ? 90 : undefined}
              />
            </IconButton>
          </Box>
          <Typography variant="h2">Rule Results</Typography>
        </Box>
      </Box>

      {expanded && (
        <Box mt={4}>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{ marginBlock: 2 }}
          >
            {availableDecisionTypes.length > 1 && (
              <FormControl size="small">
                <Select
                  sx={{ width: '180px' }}
                  value={selectedDecisionType}
                  onChange={handleDecisionTypeChange}
                >
                  {availableDecisionTypes.map(item => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedDecisionId && (
              <Box sx={{ width: '170px' }}>
                <Button
                  buttonTitle="Decision Graph"
                  variant="infoSolid"
                  onClick={() =>
                    window.open(
                      `/workbench/ppwb/policy/${policyId}/decision/${selectedDecisionId}/graph`,
                      '_blank',
                    )
                  }
                />
              </Box>
            )}
          </Box>

          {lightbulbMessages.map((message, index) => (
            <Box
              display="flex"
              alignItems="center"
              key={index}
              gap={1}
              sx={{ marginBlock: 2 }}
            >
              <FaLightbulb color="orange" size={20} />
              <Typography
                variant="body2"
                color="textSecondary"
                style={{
                  fontSize: '1.2rem',
                  verticalAlign: 'text-bottom',
                }}
              >
                {message}
              </Typography>
            </Box>
          ))}

          <Spacer height={8} />
          <Box>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              {tabs.map(tabKey => (
                <Tab
                  key={tabKey}
                  label={RuleResultTab[tabKey]}
                  value={tabKey}
                />
              ))}
            </Tabs>
            <RuleResultsView
              data={orderedRuleResultData}
              isRidersTab={selectedTab === 'RIDERS'}
              isGen2={isGen2}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface PPWBEngineDecision {
  additionalInfo: {
    calculationInfo: CalculationInfo;
  };
}

function getMessageFromDecision(
  decision: PPWBEngineDecision | undefined,
  saharaDeclineToRuw: boolean,
): string[] {
  const messageArray: string[] = [];
  if (!decision || !decision.additionalInfo) return messageArray;
  const calculationInfo = decision.additionalInfo.calculationInfo;

  if (calculationInfo?.decisionChangeReasons) {
    messageArray.push(
      ...filterVisibleDecisionChangeReasons(
        calculationInfo.decisionChangeReasons,
      ),
    );
  } else {
    // for policies before decisionChangeReasons change
    if (Boolean(calculationInfo?.movedToRuwAsReinstatementDeclinedEarlier)) {
      messageArray.push(
        'Application RUW triggered due to previous decline in Reinstatement',
      );
    }
    if (calculationInfo?.ruwDueToWorsePreviousDecision) {
      messageArray.push(calculationInfo.ruwDueToWorsePreviousDecision.reason);
    }
    if (
      calculationInfo?.eligibilityDowngrades &&
      typeof calculationInfo.eligibilityDowngrades === 'object' &&
      !Array.isArray(calculationInfo.eligibilityDowngrades) &&
      Object.keys(calculationInfo.eligibilityDowngrades).length > 0
    ) {
      messageArray.push('Eligibility Downgrade applies to this application');
    }
  }

  let containsV2Flags = false;
  if (Boolean(calculationInfo?.downgradeDetails?.isClassDowngradedDueToTrl)) {
    messageArray.push('TRL Downgrade applies to this application');
    containsV2Flags = true;
  }
  if (
    Boolean(calculationInfo?.downgradeDetails?.isClassDowngradedDueToBucketing)
  ) {
    messageArray.push('Bucketing logic applies to this application');
    containsV2Flags = true;
  }
  if (
    Boolean(
      calculationInfo?.downgradeDetails
        ?.isClassDowngradedDueToOtherThanTrlAndBucketing,
    )
  ) {
    messageArray.push('Class downgrade applies to this application');
    containsV2Flags = true;
  }
  if (!containsV2Flags && Boolean(calculationInfo?.downgradeDetails?.reason)) {
    messageArray.push(
      'Downgrades or bucketing logic applies to this application',
    );
  }
  if (saharaDeclineToRuw) {
    messageArray.push(
      'This policy contains RUW rule(s) that were originally declining',
    );
  }
  return messageArray;
}
